/* ============================================================
   Mr Candy Measurements — module global de gestion des mesures
   ============================================================
   Stockage multi-profils : une combinaison forme+longueur = un profil.
   Permet a la cliente d'avoir des mesures differentes pour, ex :
   "Coffin Medium" vs "Coffin Court" vs "Stiletto Long".

   API publique : window.MrCandyMeasurements

   Schema v2 (storage key : mrcandy_measurements_v1) :
     {
       version: 2,
       profiles: {
         "Coffin|Medium": {
           shape: "Coffin",
           length: "Medium",
           hands: { left: {...}, right: {...} },
           unit: "mm",
           saved_at: "ISO date"
         },
         ...
       },
       active_key: "Coffin|Medium"  // dernier profil charge ou enregistre
     }

   Migration auto v1 -> v2 si donnees historiques detectees.
   API legacy (save/load/has/clear) conservee pour retro-compat ;
   les nouvelles methodes (saveProfile/loadProfile/listProfiles)
   sont prefereees pour le multi-profil.
*/
(function() {
  var STORAGE_KEY = 'mrcandy_measurements_v1';
  var SCHEMA_VERSION = 2;

  var FINGER_NAMES = {
    pouce: 'Pouce',
    index: 'Index',
    majeur: 'Majeur',
    annulaire: 'Annulaire',
    auriculaire: 'Auriculaire'
  };
  var FINGER_NAMES_SHORT = {
    pouce: 'Pouce',
    index: 'Index',
    majeur: 'Majeur',
    annulaire: 'Annul.',
    auriculaire: 'Auric.'
  };
  var FINGER_ORDER = ['pouce', 'index', 'majeur', 'annulaire', 'auriculaire'];

  /* Defaults pour le selecteur forme/longueur quand on cree un profil
     depuis la fiche produit ou la section "Mes mesures". Synchro avec
     les blocs schema de cds-outfit-finder. */
  var SHAPES_DEFAULT = ['Coffin', 'Stiletto', 'Carré arrondi', 'Carré', 'Rond', 'Amande'];
  var LENGTHS_DEFAULT = ['Extra court', 'Court', 'Medium', 'Medium cœur', 'Long', 'XXL'];

  function isAvailable() {
    try {
      var t = '__mrcandy_test__';
      window.localStorage.setItem(t, '1');
      window.localStorage.removeItem(t);
      return true;
    } catch (e) {
      return false;
    }
  }

  /* Cle de profil. Convention "Shape|Length". Si shape ou length est nil
     ou vide, on utilise "_" comme placeholder pour conserver l'unicite. */
  function _profileKey(shape, length) {
    var s = (shape == null || shape === '') ? '_' : String(shape).trim();
    var l = (length == null || length === '') ? '_' : String(length).trim();
    return s + '|' + l;
  }

  /* Lecture brute du storage. Renvoie l'objet v2 ou null. */
  function _readRaw() {
    if (!isAvailable()) return null;
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return null;
      /* Migration v1 -> v2 */
      if (!data.version || data.version === 1) {
        data = _migrateV1ToV2(data);
        _writeRaw(data);
      }
      if (data.version !== SCHEMA_VERSION) {
        /* Version inconnue / future : on flush silencieusement */
        clear();
        return null;
      }
      if (!data.profiles || typeof data.profiles !== 'object') data.profiles = {};
      return data;
    } catch (e) {
      try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      return null;
    }
  }

  function _writeRaw(data) {
    if (!isAvailable()) return false;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      return true;
    } catch (e) { return false; }
  }

  /* Migration v1 (single profile flat) -> v2 (multi-profil keyed) */
  function _migrateV1ToV2(v1) {
    var key = _profileKey(v1.shape, v1.length);
    var profile = {
      shape: v1.shape || null,
      length: v1.length || null,
      hands: {
        left: (v1.hands && v1.hands.left) || {},
        right: (v1.hands && v1.hands.right) || {}
      },
      unit: v1.unit || 'mm',
      saved_at: v1.saved_at || new Date().toISOString()
    };
    var profiles = {};
    profiles[key] = profile;
    return {
      version: SCHEMA_VERSION,
      profiles: profiles,
      active_key: key
    };
  }

  /* Sanitize hand: ne garde que les 5 doigts attendus avec valeurs non-vides */
  function _sanitizeHand(hand) {
    var out = {};
    if (!hand || typeof hand !== 'object') return out;
    for (var i = 0; i < FINGER_ORDER.length; i++) {
      var f = FINGER_ORDER[i];
      var v = hand[f];
      if (v != null && String(v).trim().length > 0) out[f] = String(v).trim();
    }
    return out;
  }

  /* Au moins 1 doigt rempli sur la main (pour considerer un profil "non vide") */
  function _profileHasAnyValue(profile) {
    if (!profile || !profile.hands) return false;
    var l = profile.hands.left || {};
    var r = profile.hands.right || {};
    for (var i = 0; i < FINGER_ORDER.length; i++) {
      if (l[FINGER_ORDER[i]] && String(l[FINGER_ORDER[i]]).length) return true;
      if (r[FINGER_ORDER[i]] && String(r[FINGER_ORDER[i]]).length) return true;
    }
    return false;
  }

  /* ========== API multi-profil ========== */

  function saveProfile(shape, length, payload) {
    if (!isAvailable()) return false;
    var data = _readRaw() || { version: SCHEMA_VERSION, profiles: {}, active_key: null };
    var key = _profileKey(shape, length);
    var profile = {
      shape: (shape == null || shape === '') ? null : String(shape).trim(),
      length: (length == null || length === '') ? null : String(length).trim(),
      hands: {
        left: _sanitizeHand(payload && payload.hands && payload.hands.left),
        right: _sanitizeHand(payload && payload.hands && payload.hands.right)
      },
      unit: (payload && payload.unit) || 'mm',
      saved_at: new Date().toISOString()
    };
    if (!_profileHasAnyValue(profile)) return false;
    data.profiles[key] = profile;
    data.active_key = key;
    _writeRaw(data);
    _emit('mrcandy:measurements:saved', { key: key, profile: profile });
    return true;
  }

  function loadProfile(shape, length) {
    var data = _readRaw();
    if (!data) return null;
    var key = _profileKey(shape, length);
    var p = data.profiles[key];
    return p || null;
  }

  function hasProfile(shape, length) {
    return loadProfile(shape, length) !== null;
  }

  function removeProfile(shape, length) {
    if (!isAvailable()) return false;
    var data = _readRaw();
    if (!data) return false;
    var key = _profileKey(shape, length);
    if (!data.profiles[key]) return false;
    delete data.profiles[key];
    /* Si on supprime le profil actif, on choisit un autre comme actif (le plus recent) */
    if (data.active_key === key) {
      var keys = Object.keys(data.profiles);
      var latest = null, latestKey = null;
      keys.forEach(function(k) {
        var p = data.profiles[k];
        if (!latest || (p.saved_at && p.saved_at > latest.saved_at)) {
          latest = p; latestKey = k;
        }
      });
      data.active_key = latestKey;
    }
    _writeRaw(data);
    _emit('mrcandy:measurements:cleared', { key: key });
    return true;
  }

  /* Liste tous les profils, tries du plus recent au plus ancien.
     Renvoie [{key, shape, length, hands, unit, saved_at}, ...] */
  function listProfiles() {
    var data = _readRaw();
    if (!data) return [];
    var arr = [];
    var keys = Object.keys(data.profiles);
    keys.forEach(function(k) {
      var p = data.profiles[k];
      arr.push({
        key: k,
        shape: p.shape,
        length: p.length,
        hands: p.hands || { left: {}, right: {} },
        unit: p.unit || 'mm',
        saved_at: p.saved_at
      });
    });
    arr.sort(function(a, b) { return (b.saved_at || '').localeCompare(a.saved_at || ''); });
    return arr;
  }

  function getActiveProfile() {
    var data = _readRaw();
    if (!data || !data.active_key) return null;
    var p = data.profiles[data.active_key];
    if (!p) return null;
    return {
      key: data.active_key,
      shape: p.shape,
      length: p.length,
      hands: p.hands || { left: {}, right: {} },
      unit: p.unit || 'mm',
      saved_at: p.saved_at
    };
  }

  function setActiveProfile(shape, length) {
    if (!isAvailable()) return false;
    var data = _readRaw();
    if (!data) return false;
    var key = _profileKey(shape, length);
    if (!data.profiles[key]) return false;
    data.active_key = key;
    return _writeRaw(data);
  }

  function clear() {
    if (!isAvailable()) return false;
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      _emit('mrcandy:measurements:cleared', { all: true });
      return true;
    } catch (e) { return false; }
  }

  function hasAnyProfile() {
    var data = _readRaw();
    if (!data) return false;
    return Object.keys(data.profiles || {}).length > 0;
  }

  /* ========== Legacy API (retro-compat) ========== */

  /* Sauvegarde via l'ancien format. payload doit contenir hands/shape/length/unit. */
  function save(payload) {
    if (!payload) return false;
    return saveProfile(payload.shape, payload.length, payload);
  }

  /* Renvoie le profil actif au format legacy (compatible avec l'ancien
     code qui faisait data.hands.left.pouce). */
  function load() {
    return getActiveProfile();
  }

  function has() {
    return hasAnyProfile();
  }

  /* ========== Format helpers (inchanges) ========== */

  function formatValue(value, unit) {
    if (value == null || value === '') return '';
    unit = unit || 'mm';
    var str = String(value).trim();
    if (unit === 'mm' && /^\d+([.,]\d+)?$/.test(str)) return str + 'mm';
    return str;
  }

  /* Format hand for clipboard / display.
     - unit "mm"  : "Pouce 14mm · Index 13mm · Majeur 15mm · ..."
     - unit "kit" : "Pouce 5 · Index 6 · ... (sizing kit)" */
  function formatHand(hand, unit) {
    if (!hand) return '';
    unit = unit || 'mm';
    var parts = [];
    for (var i = 0; i < FINGER_ORDER.length; i++) {
      var key = FINGER_ORDER[i];
      var val = hand[key];
      if (val == null || String(val).length === 0) continue;
      if (unit === 'mm') {
        parts.push(FINGER_NAMES[key] + ' ' + formatValue(val, 'mm'));
      } else {
        /* Pour le kit on omet le suffixe par valeur et on ajoute un marqueur unique a la fin. */
        parts.push(FINGER_NAMES[key] + ' ' + String(val).trim());
      }
    }
    var joined = parts.join(' · ');
    if (joined && unit === 'kit') joined += ' (sizing kit)';
    return joined;
  }

  /* Label court pour badges UI : "MM" ou "KIT" */
  function unitBadge(unit) {
    return (unit === 'kit') ? 'KIT' : 'MM';
  }
  function unitLabel(unit) {
    return (unit === 'kit') ? 'Sizing kit' : 'Millimètres';
  }
  function unitHint(unit) {
    return (unit === 'kit') ? '(sizing kit)' : '(en mm)';
  }

  function lastUpdated(savedAt) {
    var src = savedAt;
    if (!src) {
      var p = getActiveProfile();
      src = p && p.saved_at;
    }
    if (!src) return null;
    try {
      var dt = new Date(src);
      var months = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
      return 'le ' + dt.getDate() + ' ' + months[dt.getMonth()] + ' ' + dt.getFullYear();
    } catch (e) { return null; }
  }

  /* Joli label "Coffin · Medium" pour affichage UI */
  function profileLabel(shape, length) {
    var s = shape || '';
    var l = length || '';
    if (s && l) return s + ' · ' + l;
    if (s) return s;
    if (l) return l;
    return 'Sans précision';
  }

  /* ========== Clipboard ========== */

  function copyToClipboard(text) {
    if (!text) return Promise.resolve(false);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).then(function() { return true; }, function() { return _legacyCopy(text); });
    }
    return Promise.resolve(_legacyCopy(text));
  }

  function _legacyCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.setAttribute('readonly', '');
      document.body.appendChild(ta);
      ta.select();
      var ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) { return false; }
  }

  function _emit(name, detail) {
    try {
      var ev;
      if (typeof CustomEvent === 'function') ev = new CustomEvent(name, { detail: detail });
      else { ev = document.createEvent('CustomEvent'); ev.initCustomEvent(name, false, false, detail); }
      window.dispatchEvent(ev);
    } catch (e) {}
  }

  /* ========== Public API ========== */

  window.MrCandyMeasurements = {
    /* Multi-profil */
    saveProfile: saveProfile,
    loadProfile: loadProfile,
    hasProfile: hasProfile,
    removeProfile: removeProfile,
    listProfiles: listProfiles,
    getActiveProfile: getActiveProfile,
    setActiveProfile: setActiveProfile,

    /* Legacy / generique */
    save: save,
    load: load,
    has: has,
    clear: clear,

    /* Helpers */
    formatHand: formatHand,
    formatValue: formatValue,
    profileLabel: profileLabel,
    unitBadge: unitBadge,
    unitLabel: unitLabel,
    unitHint: unitHint,
    lastUpdated: lastUpdated,
    isAvailable: isAvailable,
    copyToClipboard: copyToClipboard,

    /* Constantes */
    fingerOrder: FINGER_ORDER,
    fingerNames: FINGER_NAMES,
    fingerNamesShort: FINGER_NAMES_SHORT,
    shapesDefault: SHAPES_DEFAULT,
    lengthsDefault: LENGTHS_DEFAULT
  };
})();
